import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Download, Monitor, Terminal, Code2, Zap, Check, ExternalLink } from 'lucide-react';

const features = [
  "AI-powered code completion and generation",
  "Context-aware suggestions from your project",
  "Multi-model support (GPT, Claude, Gemini)",
  "Token usage tracking in the status bar",
  "Quick access to SDLC phases",
  "Inline code generation from comments",
  "Real-time collaboration with BuilderShift.ai cloud",
  "Code review and test generation commands",
];

const ides = [
  { name: "Visual Studio Code", desc: "Full extension support", status: "Available", statusColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  { name: "JetBrains IDEs", desc: "IntelliJ, PyCharm, WebStorm", status: "Coming Soon", statusColor: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  { name: "Neovim", desc: "Plugin support via LSP", status: "Coming Soon", statusColor: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  { name: "Sublime Text", desc: "Plugin package", status: "Planned", statusColor: "bg-white/5 text-white/30 border-white/10" },
];

export default function VSCodePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="vscode-page">
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center">
          <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <span className="ml-4 text-sm text-white/40">/ IDE Extensions</span>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-center mb-12">
          <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 mb-4 px-3 py-1">
            <Monitor className="w-3 h-3 mr-1.5" /> IDE Integration
          </Badge>
          <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-3" style={{fontFamily: 'Space Grotesk, sans-serif'}} data-testid="vscode-title">
            BuilderShift.ai for Your IDE
          </h1>
          <p className="text-white/40 text-lg max-w-xl mx-auto">
            Bring AI-powered code generation directly into your development environment. Full copilot mode with your BuilderShift.ai tokens.
          </p>
        </div>

        {/* Main Download Card */}
        <Card className="bg-white/[0.03] border-indigo-500/30 shadow-[0_0_30px_rgba(99,102,241,0.1)] mb-10" data-testid="vscode-download-card">
          <CardContent className="p-8 flex flex-col md:flex-row items-center gap-8">
            <div className="w-20 h-20 rounded-xl bg-indigo-500/10 flex items-center justify-center shrink-0">
              <Code2 className="w-10 h-10 text-indigo-400" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold text-white mb-1" style={{fontFamily: 'Space Grotesk, sans-serif'}}>VS Code Extension</h2>
              <p className="text-white/40 text-sm mb-3">Version 1.0.0 - Copilot mode, code gen, SDLC integration</p>
              <div className="flex items-center gap-2 flex-wrap justify-center md:justify-start">
                <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">Stable</Badge>
                <Badge variant="outline" className="text-white/30 border-white/10 text-[10px]">v1.0.0</Badge>
              </div>
            </div>
            <Button size="lg" className="bg-indigo-600 hover:bg-indigo-500 text-white h-12 px-8 shadow-[0_0_20px_rgba(99,102,241,0.3)] shrink-0" data-testid="download-vscode-btn">
              <Download className="w-5 h-5 mr-2" /> Download for VS Code
            </Button>
          </CardContent>
        </Card>

        {/* Features */}
        <h2 className="text-xl font-semibold text-white mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Copilot Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-12">
          {features.map((feat, i) => (
            <div key={i} className="flex items-center gap-3 bg-white/[0.02] border border-white/10 rounded-lg p-4" data-testid={`vscode-feature-${i}`}>
              <Check className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-sm text-white/60">{feat}</span>
            </div>
          ))}
        </div>

        {/* Other IDEs */}
        <h2 className="text-xl font-semibold text-white mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Supported IDEs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ides.map((ide, i) => (
            <Card key={i} className="bg-white/[0.03] border-white/10" data-testid={`ide-card-${i}`}>
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-white">{ide.name}</h3>
                  <p className="text-xs text-white/30">{ide.desc}</p>
                </div>
                <Badge className={ide.statusColor}>{ide.status}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
